import { Crown, Sparkles } from "lucide-react";

/**
 * Celebration banner shown to newly promoted Group Ambassadors.
 * Rendered only when the backend flag `is_ambassador` (or profile.isAmbassador) is true.
 */
export default function AmbassadorBanner({ name, title = "Group Ambassador", tierName = "Reserve" }) {
  const displayName = (name || "").trim() || "Ambassador";
  const tier = (tierName || "Reserve").trim() || "Reserve";
  return (
    <div
      data-testid="ambassador-banner"
      className="relative overflow-hidden rounded-2xl border border-gold/40 bg-gradient-to-br from-[#1a1204] via-[#2b1e05] to-[#0a0703] p-6 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.4)]"
    >
      {/* Ambient foil glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-16 -top-20 h-56 w-56 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(240,206,106,0.25) 0%, transparent 65%)",
          filter: "blur(4px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -bottom-24 h-64 w-64 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(229,193,88,0.18) 0%, transparent 60%)",
          filter: "blur(6px)",
        }}
      />
      {/* Fine guilloche */}
      <svg
        aria-hidden
        viewBox="0 0 400 120"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.06]"
      >
        <defs>
          <pattern id="amb-guilloche" width="18" height="18" patternUnits="userSpaceOnUse">
            <path d="M0 9 Q4.5 0 9 9 T18 9" fill="none" stroke="#F0CE6A" strokeWidth="0.5" />
            <path d="M0 9 Q4.5 18 9 9 T18 9" fill="none" stroke="#F0CE6A" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="400" height="120" fill="url(#amb-guilloche)" />
      </svg>

      <div className="relative flex items-start gap-4">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/60"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(240,206,106,0.35), rgba(0,0,0,0.6))",
          }}
        >
          <Crown className="h-6 w-6 text-gold" strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-gold">
            <Sparkles className="h-3 w-3" /> Congratulations
          </p>
          <h3
            className="mt-2 font-serif text-2xl leading-tight text-white sm:text-3xl"
            data-testid="ambassador-banner-name"
          >
            {displayName}, welcome to the {title}s' circle.
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/70">
            You have been newly promoted to <span className="text-gold">{title}</span>.
            Your {tier} membership is now active — enjoy priority concierge, elevated
            withdrawal thresholds and access to the ambassador roundtable.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-black/40 px-3 py-1 text-[10px] uppercase tracking-[0.3em] text-gold">
            <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            {title} · {tier}
          </div>
        </div>
      </div>
    </div>
  );
}
